// MapView component - Main Mapbox map

import React, { useEffect, useRef, useState } from 'react';
import { mapboxgl, initializeMap, isMapboxConfigured, flyTo, fitToBounds } from '../../services/mapbox';
import { LoadingOverlay } from '../ui/LoadingSpinner';
import Badge from '../ui/Badge';

const MapView = ({
  pods = [],
  allPods = [],
  outlets = [],
  searchedOutlets = [],
  selectedPod,
  onSelectPod,
  selectedRadius,
  selectedProvince,
  showOutlets = true,
  showProximityRadius = true,
  className = '',
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef({ pods: {}, outlets: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isZoomedIn, setIsZoomedIn] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    if (!isMapboxConfigured()) {
      setError('Mapbox access token not configured. Please add your token to .env file.');
      setLoading(false);
      return;
    }

    // Calculate initial center from pods if available
    let initialCenter = [28.0, -29.0]; // Default: Center of South Africa
    let initialZoom = 5;

    if (pods.length > 0) {
      const validPods = pods.filter((p) => p.longitude && p.latitude);
      if (validPods.length > 0) {
        const avgLng = validPods.reduce((sum, p) => sum + p.longitude, 0) / validPods.length;
        const avgLat = validPods.reduce((sum, p) => sum + p.latitude, 0) / validPods.length;
        initialCenter = [avgLng, avgLat];
      }
    }

    try {
      map.current = initializeMap({
        container: mapContainer.current,
        center: initialCenter,
        zoom: initialZoom,
      });

      map.current.on('load', () => {
        setMapLoaded(true);
        setLoading(false);
      });

      map.current.on('error', (e) => {
        console.error('Map error:', e);
        setError('Failed to load map. Please check your Mapbox token.');
        setLoading(false);
      });
    } catch (err) {
      console.error('Error initializing map:', err);
      setError(err.message);
      setLoading(false);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Add/update POD markers
  useEffect(() => {
    if (!mapLoaded || !map.current || pods.length === 0) return;

    // Clear existing POD markers
    Object.values(markersRef.current.pods).forEach((marker) => marker.remove());
    markersRef.current.pods = {};

    // Add new POD markers
    pods.forEach((pod) => {
      if (!pod.longitude || !pod.latitude) return;

      const el = document.createElement('div');
      el.className = `pod-marker ${selectedPod?.id === pod.id ? 'selected' : ''}`;
      const isSelected = selectedPod?.id === pod.id;
      el.innerHTML = `
        <div style="
          width: ${isSelected ? '36px' : '28px'};
          height: ${isSelected ? '36px' : '28px'};
          background: ${isSelected ? '#F97316' : '#1E3A5F'};
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        ">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
            <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
          </svg>
        </div>
      `;

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onSelectPod?.(pod);
      });

      try {
        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([pod.longitude, pod.latitude])
          .setPopup(
            new mapboxgl.Popup({ offset: 25, closeButton: true })
              .setHTML(`
                <div style="padding: 12px; min-width: 180px;">
                  <h3 style="font-weight: 600; margin-bottom: 4px; font-size: 14px;">${pod.siteNumber}</h3>
                  <p style="color: #64748B; font-size: 12px; margin: 0;">${pod.area}</p>
                  <p style="color: #94A3B8; font-size: 11px; margin-top: 4px;">${pod.streetAddress || ''}</p>
                </div>
              `)
          )
          .addTo(map.current);

        markersRef.current.pods[pod.id] = marker;
      } catch (err) {
        console.error('Error adding marker:', err);
      }
    });
  }, [mapLoaded, pods, selectedPod, onSelectPod]);

  // Add/update outlet markers
  useEffect(() => {
    if (!mapLoaded || !map.current || !showOutlets || outlets.length === 0) return;

    // Clear existing outlet markers
    Object.values(markersRef.current.outlets).forEach((marker) => marker.remove());
    markersRef.current.outlets = {};

    // Add new outlet markers
    outlets.forEach((outlet, index) => {
      if (!outlet.longitude || !outlet.latitude) return;

      const el = document.createElement('div');
      el.className = 'outlet-marker';
      el.innerHTML = `
        <div style="
          width: 24px;
          height: 24px;
          background: ${outlet.brandColor || '#10B981'};
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          cursor: pointer;
        ">
          ${outlet.brandIcon || '🍕'}
        </div>
      `;

      try {
        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([outlet.longitude, outlet.latitude])
          .setPopup(
            new mapboxgl.Popup({ offset: 25, closeButton: true })
              .setHTML(`
                <div style="padding: 12px; min-width: 180px;">
                  <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                    <span style="font-size: 20px;">${outlet.brandIcon || '🏪'}</span>
                    <h3 style="font-weight: 600; font-size: 14px; color: #1E3A5F;">${outlet.brand || outlet.name}</h3>
                  </div>
                  <p style="color: #64748B; font-size: 12px; margin: 0 0 4px 0;">${outlet.name}</p>
                  ${outlet.vicinity ? `<p style="color: #94A3B8; font-size: 11px; margin: 0 0 8px 0;">${outlet.vicinity}</p>` : ''}
                  <div style="display: flex; gap: 12px; font-size: 11px;">
                    ${outlet.rating ? `<span style="color: #F59E0B;">⭐ ${outlet.rating.toFixed(1)}</span>` : ''}
                    ${outlet.distance ? `<span style="color: #10B981;">📍 ${outlet.distance < 1000 ? Math.round(outlet.distance) + 'm' : (outlet.distance / 1000).toFixed(1) + 'km'}</span>` : ''}
                  </div>
                  ${outlet.isOpen !== null ? `<p style="font-size: 11px; margin: 8px 0 0 0; color: ${outlet.isOpen ? '#10B981' : '#EF4444'};">${outlet.isOpen ? '🟢 Open Now' : '🔴 Closed'}</p>` : ''}
                  ${outlet.isDemo ? '<p style="font-size: 10px; margin: 4px 0 0 0; color: #94A3B8; font-style: italic;">Demo data</p>' : ''}
                </div>
              `)
          )
          .addTo(map.current);

        markersRef.current.outlets[index] = marker;
      } catch (err) {
        console.error('Error adding outlet marker:', err);
      }
    });
  }, [mapLoaded, outlets, showOutlets]);

  // Fit bounds when PODS change (zoom to show filtered pods)
  useEffect(() => {
    if (!mapLoaded || !map.current || pods.length === 0) return;

    // Skip if a pod is selected (zoomed in view)
    if (selectedPod) return;

    const bounds = pods
      .filter((pod) => pod.longitude && pod.latitude)
      .map((pod) => [pod.longitude, pod.latitude]);

    if (bounds.length > 0) {
      fitToBounds(map.current, bounds, { padding: 80, maxZoom: 7 });
      setIsZoomedIn(false);
    }
  }, [mapLoaded, pods, selectedPod, selectedProvince]);

  // Fly to selected POD and center it
  useEffect(() => {
    if (!mapLoaded || !map.current || !selectedPod) return;

    // Use flyTo with offset to center the pod properly
    map.current.flyTo({
      center: [selectedPod.longitude, selectedPod.latitude],
      zoom: 14,
      duration: 1500,
      essential: true,
      offset: [0, 0], // No offset, center exactly
    });
    setIsZoomedIn(true);
  }, [mapLoaded, selectedPod]);

  // Center map on searched outlets
  useEffect(() => {
    if (!mapLoaded || !map.current || searchedOutlets.length === 0 || selectedPod) return;

    // Get valid coordinates from searched outlets
    const validOutlets = searchedOutlets.filter(
      (outlet) => outlet.longitude && outlet.latitude
    );

    if (validOutlets.length === 0) return;

    if (validOutlets.length === 1) {
      // Single outlet: fly to it
      map.current.flyTo({
        center: [validOutlets[0].longitude, validOutlets[0].latitude],
        zoom: 14,
        duration: 1500,
        essential: true,
      });
      setIsZoomedIn(true);
    } else {
      // Multiple outlets: fit bounds to show all
      const bounds = validOutlets.map((outlet) => [
        outlet.longitude,
        outlet.latitude,
      ]);
      fitToBounds(map.current, bounds, { padding: 100, maxZoom: 12 });
      setIsZoomedIn(true);
    }
  }, [mapLoaded, searchedOutlets, selectedPod]);

  // Zoom out to show all PODs
  const handleZoomOut = () => {
    if (!mapLoaded || !map.current) return;

    const podsToShow = allPods.length > 0 ? allPods : pods;
    if (podsToShow.length === 0) return;

    const bounds = podsToShow
      .filter((pod) => pod.longitude && pod.latitude)
      .map((pod) => [pod.longitude, pod.latitude]);

    if (bounds.length > 0) {
      fitToBounds(map.current, bounds, { padding: 80, maxZoom: 6 });
      setIsZoomedIn(false);
    }
  };

  // Add proximity radius circle
  useEffect(() => {
    if (!mapLoaded || !map.current || !selectedPod || !showProximityRadius) return;

    const addCircle = () => {
      const id = 'proximity-radius';
      const radius = selectedRadius || 1000;
      const center = [selectedPod.longitude, selectedPod.latitude];

      // Remove existing layers
      if (map.current.getLayer(`${id}-fill`)) {
        map.current.removeLayer(`${id}-fill`);
      }
      if (map.current.getLayer(`${id}-stroke`)) {
        map.current.removeLayer(`${id}-stroke`);
      }
      if (map.current.getSource(id)) {
        map.current.removeSource(id);
      }

      // Create circle GeoJSON
      const circle = createCircleGeoJSON(center, radius);

      map.current.addSource(id, {
        type: 'geojson',
        data: circle,
      });

      map.current.addLayer({
        id: `${id}-fill`,
        type: 'fill',
        source: id,
        paint: {
          'fill-color': '#F97316',
          'fill-opacity': 0.15,
        },
      });

      map.current.addLayer({
        id: `${id}-stroke`,
        type: 'line',
        source: id,
        paint: {
          'line-color': '#F97316',
          'line-width': 2,
          'line-opacity': 0.6,
          'line-dasharray': [2, 2],
        },
      });
    };

    // Wait for map style to be ready
    if (map.current.isStyleLoaded()) {
      addCircle();
    } else {
      map.current.on('style.load', addCircle);
    }

    return () => {
      if (map.current) {
        if (map.current.getLayer('proximity-radius-fill')) {
          map.current.removeLayer('proximity-radius-fill');
        }
        if (map.current.getLayer('proximity-radius-stroke')) {
          map.current.removeLayer('proximity-radius-stroke');
        }
        if (map.current.getSource('proximity-radius')) {
          map.current.removeSource('proximity-radius');
        }
      }
    };
  }, [mapLoaded, selectedPod, selectedRadius, showProximityRadius]);

  // Handle error state
  if (error) {
    return (
      <div className={`bg-dark-100 rounded-xl flex items-center justify-center ${className}`}>
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 bg-dark-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-dark-700 mb-2">Map Unavailable</h3>
          <p className="text-dark-500 mb-4">{error}</p>
          <div className="bg-dark-800 text-white p-4 rounded-lg text-left">
            <p className="text-sm mb-2">Add to <code className="bg-dark-700 px-1 rounded">.env</code>:</p>
            <code className="text-xs text-accent-400 block">
              VITE_MAPBOX_ACCESS_TOKEN=pk.your_token_here
            </code>
          </div>
          <p className="text-xs text-dark-400 mt-3">
            Get a free token at <a href="https://account.mapbox.com/" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">mapbox.com</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative rounded-xl overflow-hidden shadow-map bg-dark-200 ${className}`}>
      {loading && <LoadingOverlay message="Loading map..." />}

      <div ref={mapContainer} className="w-full h-full" />

      {/* Legend */}
      {mapLoaded && (
        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 z-10">
          <h4 className="text-xs font-semibold text-dark-700 mb-2">Legend</h4>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary-500 border border-white shadow-sm" />
              <span className="text-xs text-dark-600">POD Location</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-secondary-500 border border-white shadow-sm" />
              <span className="text-xs text-dark-600">Selected POD</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-accent-500 border border-white shadow-sm" />
              <span className="text-xs text-dark-600">Fast Food</span>
            </div>
          </div>
        </div>
      )}

      {/* Stats badge */}
      {mapLoaded && pods.length > 0 && (
        <div className="absolute top-4 left-4 z-10 flex gap-2">
          <Badge variant="primary" className="bg-white/95 backdrop-blur-sm text-dark-700">
            {pods.length} PODS | {outlets.length} Outlets
          </Badge>
        </div>
      )}

      {/* Zoom out button */}
      {mapLoaded && isZoomedIn && (
        <button
          onClick={handleZoomOut}
          className="absolute top-4 right-16 z-10 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-3 py-2 text-sm font-medium text-dark-700 hover:bg-white transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
          </svg>
          Zoom Out
        </button>
      )}
    </div>
  );
};

// Helper function to create circle GeoJSON
const createCircleGeoJSON = (center, radiusMeters, steps = 64) => {
  const coordinates = [];
  const km = radiusMeters / 1000;

  for (let i = 0; i < steps; i++) {
    const angle = (i * 360) / steps;
    const radians = (angle * Math.PI) / 180;

    const dx = km * Math.cos(radians);
    const dy = km * Math.sin(radians);

    const lat = center[1] + dy / 110.574;
    const lon = center[0] + dx / (111.32 * Math.cos((center[1] * Math.PI) / 180));

    coordinates.push([lon, lat]);
  }

  coordinates.push(coordinates[0]);

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates],
    },
  };
};

export default MapView;
